/**
 * AI Providers & Saved Extraction Admin API client.
 */

export interface AiProviderConfig {
  id: string;
  name: string;
  enabled: boolean;
  type: string;
  api_key?: string;
  api_key_masked?: string;
  has_key?: boolean;
  model: string;
  base_url?: string;
  requests_per_minute: number;
  requests_per_day: number;
  max_input_tokens: number;
  priority: number;
  timeout?: number;
}

export interface AiProvidersSettingsResponse {
  active_mode: string; // 'auto' or provider id
  providers: Record<string, AiProviderConfig>;
  ollama_warning: string;
}

// ─── Table-based AI Provider (new CRUD endpoints) ─────────────────────────────

export interface AiProviderRow {
  id: string;
  name: string;
  provider_type: string; // gemini|groq|mistral|openrouter|ollama|openai_compatible
  base_url: string;
  model: string;
  api_key_masked: string;
  has_key: boolean;
  enabled: boolean;
  priority: number;
  rpm: number | null;
  rpd: number | null;
  tpd: number | null;
  is_default: boolean;
  last_test_status: string | null;
  last_test_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AiProviderCreatePayload {
  name: string;
  provider_type: string;
  base_url: string;
  model: string;
  api_key: string;
  enabled: boolean;
  priority: number;
  rpm?: number | null;
  rpd?: number | null;
  tpd?: number | null;
  is_default: boolean;
}

export interface AiProviderUpdatePayload {
  name?: string;
  provider_type?: string;
  base_url?: string;
  model?: string;
  api_key?: string;
  enabled?: boolean;
  priority?: number;
  rpm?: number | null;
  rpd?: number | null;
  tpd?: number | null;
  is_default?: boolean;
}

// Provider type presets for the Add/Edit form
export const PROVIDER_TYPE_PRESETS: Record<string, { base_url: string; model: string }> = {
  gemini: { base_url: "", model: "gemini-2.5-flash" },
  groq: { base_url: "https://api.groq.com/openai/v1", model: "llama-3.3-70b-versatile" },
  mistral: { base_url: "https://api.mistral.ai/v1", model: "mistral-small-latest" },
  openrouter: { base_url: "https://openrouter.ai/api/v1", model: "meta-llama/llama-3.3-70b-instruct:free" },
  ollama: { base_url: "http://localhost:11434/v1", model: "qwen2.5:3b" },
  openai_compatible: { base_url: "", model: "" },
};

export async function fetchAiProviderList(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string
): Promise<AiProviderRow[]> {
  return requestFn<AiProviderRow[]>("/api/v1/admin/ai-providers", {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function createAiProvider(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string,
  payload: AiProviderCreatePayload
): Promise<AiProviderRow> {
  return requestFn<AiProviderRow>("/api/v1/admin/ai-providers", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function updateAiProviderRow(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string,
  id: string,
  payload: AiProviderUpdatePayload
): Promise<AiProviderRow> {
  return requestFn<AiProviderRow>(`/api/v1/admin/ai-providers/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function deleteAiProvider(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string,
  id: string
): Promise<{ success: boolean }> {
  return requestFn<{ success: boolean }>(`/api/v1/admin/ai-providers/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function testAiProviderById(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string,
  id: string
): Promise<{ success: boolean; provider: string; model?: string; message: string }> {
  return requestFn<{ success: boolean; provider: string; model?: string; message: string }>(
    `/api/v1/admin/ai-providers/${encodeURIComponent(id)}/test`,
    { method: "POST", headers: { Authorization: `Bearer ${token}` } }
  );
}

export async function reorderAiProviders(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string,
  orderedIds: string[]
): Promise<{ success: boolean; message: string }> {
  return requestFn<{ success: boolean; message: string }>("/api/v1/admin/ai-providers/reorder", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });
}

// ─── Legacy settings-based endpoints (kept for backwards compat) ──────────────

export interface SavedExtractionSection {
  id: string;
  section_index: number;
  title: string;
  start_page: number;
  end_page: number;
  word_count: number;
  status: string;
  warnings: string[];
}

export interface SavedExtractionStatusResponse {
  has_saved_extraction: boolean;
  is_outdated: boolean;
  meta: {
    pdf_hash: string;
    extractor_name: string;
    date: string;
    page_count: number;
    warnings: string[];
    sections_count: number;
  } | null;
  sections_count?: number;
  warnings_count?: number;
  sections: SavedExtractionSection[];
}

export async function fetchAiProviders(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string
): Promise<AiProvidersSettingsResponse> {
  return requestFn<AiProvidersSettingsResponse>("/api/v1/admin/settings/ai-providers", {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function updateAiProviders(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string,
  payload: { active_mode: string; providers: Record<string, any> }
): Promise<{ success: boolean; message: string }> {
  return requestFn<{ success: boolean; message: string }>("/api/v1/admin/settings/ai-providers", {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export async function testAiProvider(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string,
  provider: string
): Promise<{ success: boolean; provider: string; model?: string; message: string }> {
  return requestFn<{ success: boolean; provider: string; model?: string; message: string }>(
    `/api/v1/admin/settings/ai-providers/test/${encodeURIComponent(provider)}`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export async function fetchSavedExtractionStatus(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string,
  courseId: string,
  moduleId: string
): Promise<SavedExtractionStatusResponse> {
  return requestFn<SavedExtractionStatusResponse>(
    `/api/v1/admin/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(moduleId)}/extraction-status`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export async function retrySavedSection(
  requestFn: <T>(path: string, options?: RequestInit) => Promise<T>,
  token: string,
  courseId: string,
  moduleId: string,
  sectionId: string
): Promise<{ success: boolean; message: string }> {
  return requestFn<{ success: boolean; message: string }>(
    `/api/v1/admin/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(moduleId)}/sections/${encodeURIComponent(sectionId)}/retry`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}
